import { Modal, useModal } from '@gluedigital/modal';
import { ReactNode, useEffect } from 'react';
import { Outlet } from 'react-router';
import NewChatButton from 'src/components/Chat/NewChatButton/NewChatButton';
import ShareChatButton from 'src/components/Chat/ShareChatButton/ShareChatButton';
import ExperimentalLabel from 'src/components/ExperimentalLabel/ExperimentalLabel';
import SelectUserFront from 'src/components/modals/SelectUserModal/SelectUserFront/SelectUserFront';
import SelectAiModel from 'src/components/SelectAIModel/SelectAIModel';
import Sidebar from 'src/components/Sidebar/Sidebar';
import SuccessToast from 'src/components/SuccessToast/SuccessToast';
import WidgetThumb from 'src/components/Widget/WidgetThumb/WidgetThumb';
import { StreamingContextProvider } from 'src/context/StreamingContext';
import { useClientConfig } from 'src/hooks/useClientConfig';
import { useIsBubbleWidget } from 'src/hooks/useIsBubbleWidget';
import { useIsEmbedded } from 'src/hooks/useIsEmbedded';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { useSidebar } from 'src/hooks/useSidebar';
import { useSuccessToast } from 'src/hooks/useSuccessToast';
import logo from 'src/static/vectors/logo-block.svg';
import { handleRemoveSources } from 'src/store/actions/sources';
import { BotMessage, FromMessageEnum, Message } from 'src/types';
import { useAppDispatch, useAppSelector } from 'universal-scripts';
import IntroAnimation from './IntroAnimation/IntroAnimation';

import 'src/components/modals/SelectUserModal/SelectUserModal';
import './Home.sass';

interface HomeLayoutProps {
	children: ReactNode;
}

export const HomeLayout = ({ children }: HomeLayoutProps) => {
	const isShown = useAppSelector((s) => s.animation);
	const messages = useAppSelector((s) => s?.messages);
	const firstBotResponse = (messages as Message[]).find((m): m is BotMessage => m.from === FromMessageEnum.BOT);

	const hasResponse = (message?: BotMessage) => {
		return !(message?.completed && message?.text);
	};

	const scroll = useAppSelector((s) => s.hasScroll);
	const modal = useModal();
	const dispatch = useAppDispatch();
	const isMobile = useIsMobile();
	const { successMessage, closeSuccessToast } = useSuccessToast();

	const { handleShowSidebar, showSidebar } = useSidebar();
	const isBubbleWidget = useIsBubbleWidget();
	const isEmbedded = useIsEmbedded();
	const hideHostChrome = isBubbleWidget || isEmbedded;
	const client = useClientConfig();

	useEffect(() => {
		if (!hideHostChrome && __DEV__ && !client) {
			modal.show('select-user-modal');
		}
	}, [modal, hideHostChrome, client]);

	useEffect(() => {
		if (isShown === true) return;
		const id = setTimeout(() => {
			dispatch({ type: 'animation/shown' });
			document.cookie = `animationShown=true;maxAge:31536000`;
		}, 4600);
		return () => clearTimeout(id);
	}, [isShown, dispatch]);

	if (!isShown && !hideHostChrome && !client) {
		return (
			<div id="home" className="page show-animation">
				<IntroAnimation />
			</div>
		);
	}

	const openSidebar = () => {
		dispatch(handleRemoveSources());
		handleShowSidebar();
	};

	return (
		<StreamingContextProvider>
			<div id="home" className="page">
				<Sidebar handleClose={handleShowSidebar} show={showSidebar} />
				<header id="home-masthead" className={`${scroll ? 'has-scroll' : ''} ${hideHostChrome ? 'widget-mode' : ''}`}>
					<section id="mobile-logo" onClick={openSidebar} role="button">
						{hideHostChrome ? <WidgetThumb /> : <img src={client?.image?.toString() ?? logo} alt="logo" />}
						<ExperimentalLabel mini />
					</section>
					<section id="header-buttons">
						{!hideHostChrome && <ShareChatButton disabled={hasResponse(firstBotResponse)} />}
						<NewChatButton disabled={hasResponse(firstBotResponse)} />
					</section>
				</header>
				<section className={`main-content ${isMobile && showSidebar ? 'hide' : ''}`}>
					{children}
					{!hideHostChrome && <SelectAiModel />}
					{!hideHostChrome && <SelectUserFront />}
				</section>
				<Modal>{<div />}</Modal>
				<SuccessToast successMessage={successMessage} closeSuccessToast={closeSuccessToast} />
			</div>
		</StreamingContextProvider>
	);
};

const Home = () => {
	return (
		<HomeLayout>
			<Outlet />
		</HomeLayout>
	);
};

export default Home;
